import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';
import {
  AddIconWoPadding,
  CaretRightIcon,
  CompanyIcon,
  CustomersTabIcon,
  EditIcon,
  InstitutionIcon,
  MailIIcon,
  MapIcon,
  PhoneIcon
} from 'assets/icons';
import { clevertapEvents } from 'types/baseTypes';
import { InfoMesssages } from 'types/infoMessages';
import { logAnalyticEvent } from 'utils/analytics';
import { CustomerDetails } from '../invoices.model';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import { getAddressAsString } from 'containers/jobs/utils';
import AddressModal from 'containers/jobs/components/AddressModal';
import { AddCustomerAddressPayload } from 'containers/customers/customers.model';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAddCustomerAddressMutation, useEditCustomerAddressMutation } from 'containers/customers/api';

interface CustomerContactInformationWidgetProps {
  customerData: CustomerDetails;
  isEditCustomerAllowed: boolean;
  openEditCustomerModal: () => void;
  onInfoClick: (id: string, infoMessage: InfoMesssages) => void;
  refreshCustomerDetails: () => void;
  isEditable: boolean;
}

const CustomerContactInformationWidget: React.FC<CustomerContactInformationWidgetProps> = ({
  customerData,
  isEditCustomerAllowed,
  openEditCustomerModal,
  onInfoClick,
  refreshCustomerDetails,
  isEditable
}) => {
  const [addCustomerAddressMutation, { isSuccess: customerAddressSuccess, isLoading: isLoadingAddAddress }] =
    useAddCustomerAddressMutation();
  const [editCustomerAddressMutation, { isSuccess: isEditAddressSuccess, isLoading: isLoadingEditAddress }] =
    useEditCustomerAddressMutation();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const initialAddressModalState = {
    type: null,
    isOpen: false,
    isEdit: false,
    address: null
  };

  const [openAddressModal, setOpenAddressModal] = useState<{
    type: ADDRESS_TYPE;
    isOpen: boolean;
    isEdit: boolean;
    address;
  }>(initialAddressModalState);

  useEffect(() => {
    if (customerAddressSuccess || isEditAddressSuccess) refreshCustomerDetails();
  }, [customerAddressSuccess, isEditAddressSuccess]);

  const personalInfo = customerData
    ? [
        {
          id: 'customer-side-panel-name',
          title: 'Name',
          data: `${customerData?.name} ${customerData?.lastName}`,
          icon: <CustomersTabIcon className="path-fill-current path-stroke-current h-6 w-6 text-border" />
        },
        {
          id: 'customer-side-panel-phone',
          title: 'Phone',
          data: customerData?.phoneNumber || '-',
          icon: <PhoneIcon className="path-fill-current path text-border" />
        },
        {
          id: 'customer-side-panel-email',
          title: 'Email',
          data: customerData?.email || '-',
          icon: <MailIIcon className="path-fill-current text-border" />
        },
        {
          id: 'customer-side-panel-company',
          title: 'Company',
          data: customerData?.companyName || '-',
          icon: <CompanyIcon />
        }
      ]
    : [];

  const handleOpenAddressModal = ({ type, isEdit, address }: { type: ADDRESS_TYPE; isEdit: boolean; address }) => {
    setOpenAddressModal({ type, isOpen: true, isEdit, address });
  };

  const handleCloseAddressModal = () => {
    setOpenAddressModal(initialAddressModalState);
  };

  const onAddressAddition = async addressDetails => {
    const addressPayload: AddCustomerAddressPayload = {
      ...addressDetails,
      teamId: currentTeam.id.toString(),
      customerId: customerData?.id,
      isBillingSameAsService: false,
      addressId: 0
    };
    if (openAddressModal.isEdit) {
      await editCustomerAddressMutation({ id: openAddressModal.address?.id, body: addressPayload });
    } else {
      await addCustomerAddressMutation(addressPayload);
    }
    handleCloseAddressModal();
  };

  return (
    <div className="mx-4 rounded-md border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="text-sbase font-bold leading-[15px] text-primaryText">Contact Information</div>
        {isEditable && (
          <div
            id="customer-details-edit-customer-button"
            className={twMerge(
              'flex cursor-pointer items-center gap-3 text-sbase font-semibold text-secondary',
              classnames({ 'opacity-50': !isEditCustomerAllowed })
            )}
            onClick={() => {
              if (isEditCustomerAllowed) {
                logAnalyticEvent(clevertapEvents.webCustomersListDetailsPersonalInformationEdit, {
                  'Customer Id': customerData?.id
                });
                openEditCustomerModal();
              } else {
                onInfoClick('customer-details-edit-customer-button', InfoMesssages.editCustomerNoPermissionMessage);
              }
            }}>
            <EditIcon className="path-fill-current path" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 border-b border-[#F2F2F2] py-4">
        {personalInfo.map((info, index) => (
          <div key={info.title + index} className="flex items-center gap-4">
            {info.icon}
            <div className="flex flex-col gap-1">
              <div className="text-px13 leading-[17px] text-accentText">{info.title}</div>
              <div
                id={info.id}
                className="break-words text-sbase font-semibold leading-[18px] tracking-[-0.3px] text-primaryText">
                {info.data}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="border-b border-[#F2F2F2] py-4">
        <div className="mb-4 text-sbase font-bold tracking-[-0.3px] text-primaryText">Service Address</div>
        <div
          className={`flex w-full items-center justify-between ${
            isEditable && isEditCustomerAllowed ? 'cursor-pointer' : ''
          }`}
          onClick={() => {
            if (!isEditable || !isEditCustomerAllowed) return;
            const isEdit = !!customerData?.serviceAddress?.length;
            handleOpenAddressModal({
              type: ADDRESS_TYPE.SERVICE,
              isEdit,
              address: isEdit ? customerData.serviceAddress[0] : null
            });
          }}>
          <div className="flex items-center gap-4">
            <MapIcon className="path-fill-current shrink-0 text-border" />
            {customerData?.serviceAddress?.length ? (
              <div
                className="traking-[-0.3px] flex break-words text-left text-sbase
                font-semibold leading-[22px] text-primaryText">
                {getAddressAsString(customerData?.serviceAddress[0])}
              </div>
            ) : (
              <div
                className={`flex items-center gap-1 text-sbase font-semibold tracking-[-0.3px] text-secondary
                             ${isEditCustomerAllowed && isEditable ? 'opacity-100' : 'opacity-50'}`}>
                <AddIconWoPadding className="mt-0.5" /> Add service address
              </div>
            )}
          </div>
          {isEditable && isEditCustomerAllowed && <CaretRightIcon className="path-fill-current shrink-0 text-gray-1" />}
        </div>
      </div>
      <div className="pt-4">
        <div className="mb-4 text-sbase font-bold tracking-[-0.3px] text-primaryText">Billing Address</div>
        <div
          className={`flex w-full items-center justify-between 
                    ${isEditable && isEditCustomerAllowed ? 'cursor-pointer' : ''}`}
          onClick={() => {
            if (!isEditable || !isEditCustomerAllowed) return;
            const isEdit = !!customerData?.billingAddress;
            handleOpenAddressModal({
              type: ADDRESS_TYPE.BILLING,
              isEdit,
              address: isEdit ? customerData?.billingAddress : null
            });
          }}>
          <div className="flex items-center gap-4">
            <InstitutionIcon className="shrink-0" />
            {customerData?.billingAddress ? (
              <div
                className="traking-[-0.3px] flex break-words text-left text-sbase
                font-semibold leading-[22px] text-primaryText">
                {getAddressAsString(customerData?.billingAddress)}
              </div>
            ) : (
              <div
                className={`flex items-center gap-1 text-sbase font-semibold tracking-[-0.3px] text-secondary
                             ${(!isEditable || !isEditCustomerAllowed) && 'pointer-events-none opacity-50'}`}>
                <AddIconWoPadding className="mt-0.5" /> Add billing address
              </div>
            )}
          </div>
          {isEditable && isEditCustomerAllowed && <CaretRightIcon className="path-fill-current shrink-0 text-gray-1" />}
        </div>
      </div>
      {openAddressModal.isOpen && (
        <AddressModal
          isEdit={openAddressModal.isEdit}
          type={openAddressModal.type}
          onClose={handleCloseAddressModal}
          initialAddress={openAddressModal.address}
          onAddAddress={onAddressAddition}
          isLoading={isLoadingAddAddress || isLoadingEditAddress}
        />
      )}
    </div>
  );
};

export default CustomerContactInformationWidget;
